<template>
  <div ref="rootRef">
    <!-- Main toolbar row -->
    <div class="flex items-center gap-2 mb-3">
      <input
        ref="searchInputEl"
        :value="searchQuery"
        @input="$emit('update:searchQuery', $event.target.value)"
        @keydown.esc="$event.target.blur()"
        type="text"
        autocomplete="off"
        :placeholder="searchPlaceholder"
        class="flex-1 min-w-0 px-3 py-2 text-sm border border-stroke-strong dark:border-stroke-strong-dark rounded-md bg-surface dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark placeholder-fg-muted dark:placeholder-fg-muted-dark"
      />

      <div class="flex items-center gap-2 flex-shrink-0">

        <!-- Filter button + panel -->
        <div class="relative">
          <button
            @click.stop="filterPanelOpen = !filterPanelOpen"
            title="Filter"
            :class="[
              'px-3 py-2 text-sm border rounded-md transition-colors flex items-center gap-1.5',
              activeFilters.length
                ? 'border-blue-400 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                : 'border-stroke-strong dark:border-stroke-strong-dark bg-surface dark:bg-surface-muted-dark text-fg-secondary dark:text-fg-secondary-dark hover:bg-surface-hover dark:hover:bg-surface-hover-dark'
            ]"
          >
            <FunnelIcon class="h-4 w-4 flex-shrink-0" />
            <span class="hidden md:inline">Filter</span>
            <span
              v-if="activeFilters.length"
              class="inline-flex items-center justify-center h-4 min-w-[1rem] px-1 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
            >{{ activeFilters.length }}</span>
          </button>

          <!-- Filter panel -->
          <div
            v-if="filterPanelOpen"
            class="absolute right-0 mt-1 w-max min-w-[400px] max-w-[90vw] rounded-lg bg-surface dark:bg-surface-dark shadow-lg ring-1 ring-black/5 dark:ring-stroke-dark z-20"
            @click.stop
          >
            <div class="px-3 py-2 border-b border-stroke-subtle dark:border-stroke-dark">
              <span class="text-xs font-semibold text-fg-muted dark:text-fg-muted-dark uppercase tracking-wider">Add filter</span>
            </div>
            <div class="p-3 flex flex-wrap items-end gap-2">
              <!-- Column -->
              <div class="flex flex-col gap-1">
                <label class="text-xs text-fg-muted dark:text-fg-muted-dark">Column</label>
                <select
                  v-model="pendingFilter.col"
                  class="px-2 py-1.5 text-sm border border-stroke-strong dark:border-stroke-strong-dark rounded-md bg-surface dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark"
                >
                  <option v-for="col in filterableColumns" :key="col.key" :value="col.key">{{ col.label }}</option>
                </select>
              </div>
              <!-- Operator -->
              <div class="flex flex-col gap-1">
                <label class="text-xs text-fg-muted dark:text-fg-muted-dark">Operator</label>
                <select
                  v-model="pendingFilter.op"
                  class="px-2 py-1.5 text-sm border border-stroke-strong dark:border-stroke-strong-dark rounded-md bg-surface dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark"
                >
                  <option v-for="op in pendingOps" :key="op" :value="op">{{ op }}</option>
                </select>
              </div>
              <!-- Value -->
              <div v-if="showValueInput" class="flex flex-col gap-1">
                <label class="text-xs text-fg-muted dark:text-fg-muted-dark">Value</label>
                <select
                  v-if="pendingValueOptions"
                  v-model="pendingFilter.value"
                  class="px-2 py-1.5 text-sm border border-stroke-strong dark:border-stroke-strong-dark rounded-md bg-surface dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark"
                >
                  <option v-for="v in pendingValueOptions" :key="v" :value="v">{{ v }}</option>
                </select>
                <input
                  v-else-if="isDateInputOp"
                  v-model="pendingFilter.value"
                  type="date"
                  class="px-2 py-1.5 text-sm border border-stroke-strong dark:border-stroke-strong-dark rounded-md bg-surface dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark"
                  @keydown.enter="addFilter"
                />
                <div v-else-if="isRangeOp" class="flex items-center gap-1.5">
                  <input
                    v-model="pendingFilter.value.start"
                    type="date"
                    class="px-2 py-1.5 text-sm border border-stroke-strong dark:border-stroke-strong-dark rounded-md bg-surface dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark"
                    @keydown.enter="addFilter"
                  />
                  <span class="text-xs text-fg-faint dark:text-fg-faint-dark">to</span>
                  <input
                    v-model="pendingFilter.value.end"
                    type="date"
                    class="px-2 py-1.5 text-sm border border-stroke-strong dark:border-stroke-strong-dark rounded-md bg-surface dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark"
                    @keydown.enter="addFilter"
                  />
                </div>
                <input
                  v-else-if="isNumberInputOp"
                  v-model="pendingFilter.value"
                  type="number"
                  step="any"
                  placeholder="Value…"
                  class="px-2 py-1.5 text-sm border border-stroke-strong dark:border-stroke-strong-dark rounded-md bg-surface dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark placeholder-fg-muted dark:placeholder-fg-muted-dark w-32"
                  @keydown.enter="addFilter"
                />
                <input
                  v-else
                  v-model="pendingFilter.value"
                  type="text"
                  placeholder="Value…"
                  class="px-2 py-1.5 text-sm border border-stroke-strong dark:border-stroke-strong-dark rounded-md bg-surface dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark placeholder-fg-muted dark:placeholder-fg-muted-dark w-40"
                  @keydown.enter="addFilter"
                />
              </div>
              <!-- Add -->
              <button
                @click="addFilter"
                :disabled="!canAdd"
                class="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 dark:disabled:bg-blue-900 disabled:cursor-not-allowed text-white rounded-md transition-colors font-medium self-end"
              >Add</button>
            </div>
          </div>
        </div>

        <!-- Column picker -->
        <div class="relative">
          <button
            @click.stop="colPickerOpen = !colPickerOpen"
            title="Columns"
            class="px-3 py-2 text-sm border border-stroke-strong dark:border-stroke-strong-dark rounded-md bg-surface dark:bg-surface-muted-dark text-fg-secondary dark:text-fg-secondary-dark hover:bg-surface-hover dark:hover:bg-surface-hover-dark transition-colors flex items-center gap-1.5"
          >
            <ViewColumnsIcon class="h-4 w-4 flex-shrink-0" />
            <span class="hidden md:inline">Columns</span>
            <span class="hidden md:inline text-xs text-fg-faint dark:text-fg-faint-dark">{{ visibleCount }}</span>
          </button>

          <!-- Column picker dropdown -->
          <div
            v-if="colPickerOpen"
            class="absolute right-0 mt-1 w-56 rounded-lg bg-surface dark:bg-surface-dark shadow-lg ring-1 ring-black/5 dark:ring-stroke-dark z-20"
            @click.stop
          >
            <div class="flex items-center justify-between px-3 py-2 border-b border-stroke-subtle dark:border-stroke-dark">
              <span class="text-xs font-semibold text-fg-muted dark:text-fg-muted-dark uppercase tracking-wider">Columns</span>
              <button @click="$emit('reset-columns')" class="text-xs text-blue-600 dark:text-blue-400 hover:underline">Reset</button>
            </div>
            <div class="py-1 max-h-80 overflow-y-auto">
              <!-- Reorderable columns -->
              <div
                v-for="col in pickerColumns"
                :key="col.key"
                draggable="true"
                @dragstart="onDragStart($event, col.key)"
                @dragover="onDragOver($event, col.key)"
                @dragleave="onDragLeave"
                @drop="onDrop($event, col.key)"
                @dragend="onDragEnd"
                class="flex items-center gap-2.5 px-3 py-1.5 select-none border-t-2 hover:bg-surface-hover dark:hover:bg-surface-hover-dark"
                :class="dragOverKey === col.key ? 'border-blue-500' : 'border-transparent'"
              >
                <svg class="h-3.5 w-3.5 cursor-grab text-fg-faint dark:text-fg-faint-dark flex-shrink-0" fill="currentColor" viewBox="0 0 8 14">
                  <circle cx="2" cy="2"  r="1.4"/><circle cx="6" cy="2"  r="1.4"/>
                  <circle cx="2" cy="7"  r="1.4"/><circle cx="6" cy="7"  r="1.4"/>
                  <circle cx="2" cy="12" r="1.4"/><circle cx="6" cy="12" r="1.4"/>
                </svg>
                <input
                  type="checkbox"
                  :id="`col-pick-${col.key}`"
                  :checked="visibleKeys.includes(col.key)"
                  @change="toggleCol(col.key)"
                  class="rounded border-stroke-strong dark:border-stroke-strong-dark text-blue-600 flex-shrink-0"
                />
                <label :for="`col-pick-${col.key}`" class="text-sm text-fg-secondary dark:text-fg-secondary-dark cursor-pointer flex-1">
                  {{ col.label }}
                </label>
              </div>
              <!-- "Coming soon" columns -->
              <template v-if="comingColumns.length">
                <div class="border-t border-stroke-subtle dark:border-stroke-dark my-1" />
                <div
                  v-for="col in comingColumns"
                  :key="col.key"
                  class="flex items-center gap-2.5 px-3 py-1.5 select-none opacity-50"
                >
                  <svg class="h-3.5 w-3.5 text-stroke-strong dark:text-stroke-strong-dark flex-shrink-0" fill="currentColor" viewBox="0 0 8 14">
                    <circle cx="2" cy="2"  r="1.4"/><circle cx="6" cy="2"  r="1.4"/>
                    <circle cx="2" cy="7"  r="1.4"/><circle cx="6" cy="7"  r="1.4"/>
                    <circle cx="2" cy="12" r="1.4"/><circle cx="6" cy="12" r="1.4"/>
                  </svg>
                  <input type="checkbox" disabled class="rounded border-stroke-strong dark:border-stroke-strong-dark flex-shrink-0" />
                  <span class="text-sm text-fg-muted dark:text-fg-muted-dark flex-1">{{ col.label }}</span>
                  <span class="text-xs text-fg-faint">Soon</span>
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- Saved views -->
        <SavedViewsMenu
          v-if="savedViews !== null"
          :saved-views="savedViews"
          @save="$emit('save-view', $event)"
          @load="$emit('load-view', $event)"
          @remove="$emit('remove-view', $event)"
        />

        <!-- Refresh -->
        <button
          @click="$emit('refresh')"
          :disabled="isLoading"
          title="Refresh"
          class="px-3 py-2 text-sm bg-surface-muted dark:bg-surface-muted-dark hover:bg-surface-muted dark:hover:bg-surface-muted-dark text-fg-secondary dark:text-fg-secondary-dark rounded-md transition-colors flex items-center gap-1.5 disabled:opacity-50"
        >
          <ArrowPathIcon :class="['h-4 w-4 flex-shrink-0', isLoading && 'animate-spin']" />
          <span class="hidden md:inline">Refresh</span>
        </button>

        <!-- Extra actions slot (sync button, etc.) -->
        <slot name="actions" />
      </div>
    </div>

    <!-- Active filter chips -->
    <div v-if="activeFilters.length" class="flex flex-wrap items-center gap-1.5 mb-3">
      <span
        v-for="(f, i) in activeFilters"
        :key="i"
        class="inline-flex items-center gap-1 pl-2.5 pr-1 py-0.5 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-full text-xs text-blue-800 dark:text-blue-200"
      >
        {{ chipLabel(f) }}
        <button
          @click="removeFilter(i)"
          class="flex-shrink-0 h-4 w-4 flex items-center justify-center rounded-full text-blue-400 hover:text-white hover:bg-blue-500 transition-colors leading-none"
          aria-label="Remove filter"
        >&times;</button>
      </span>
      <button
        @click="clearAllFilters"
        class="text-xs text-fg-faint dark:text-fg-faint-dark hover:text-fg-secondary dark:hover:text-fg-secondary-dark underline ml-0.5"
      >Clear all</button>
      <span v-if="$slots.count" class="text-xs text-fg-muted dark:text-fg-muted-dark ml-auto">
        <slot name="count" />
      </span>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, onActivated, onDeactivated } from 'vue'
import { FunnelIcon, ViewColumnsIcon, ArrowPathIcon } from '@heroicons/vue/24/outline'
import { useActiveSearchFocus } from '../../composables/useActiveSearchFocus.js'
import { DEFAULT_OPS, NO_VALUE_OPS, DATE_INPUT_OPS, NUMBER_INPUT_OPS, RANGE_OPS } from '../../composables/useTableControls.js'
import SavedViewsMenu from './SavedViewsMenu.vue'

const props = defineProps({
  columns:          { type: Array,   required: true },
  searchKey:        { type: String,  default: null },  // see useActiveSearchFocus.focusNamed
  searchQuery:      { type: String,  default: '' },
  activeFilters:    { type: Array,   default: () => [] },
  visibleKeys:      { type: Array,   required: true },
  columnOrder:      { type: Array,   required: true },
  isLoading:        { type: Boolean, default: false },
  searchPlaceholder:{ type: String,  default: 'Search…' },
  savedViews:       { type: Array,   default: null },
})

const emit = defineEmits([
  'update:searchQuery',
  'update:activeFilters',
  'update:visibleKeys',
  'update:columnOrder',
  'refresh',
  'reset-columns',
  'save-view',
  'load-view',
  'remove-view',
])

// ── Derived column lists ──────────────────────────────────────────────────────
const filterableColumns = computed(() => props.columns.filter(c => !c.coming))
const pickerColumns     = computed(() => props.columnOrder.map(k => props.columns.find(c => c.key === k)).filter(c => c && !c.coming))
const comingColumns     = computed(() => props.columns.filter(c => c.coming))
const visibleCount      = computed(() => props.visibleKeys.length)

// ── Column picker open state ──────────────────────────────────────────────────
const colPickerOpen   = ref(false)
const filterPanelOpen = ref(false)

// ── Pending filter (internal state) ──────────────────────────────────────────
const _firstFilterable = computed(() => filterableColumns.value[0]?.key || '')
const pendingFilter = ref({ col: _firstFilterable.value, op: DEFAULT_OPS[0], value: '' })

const pendingCol          = computed(() => props.columns.find(c => c.key === pendingFilter.value.col))
const pendingOps          = computed(() => pendingCol.value?.ops || DEFAULT_OPS)
const _resolveOpts = opts => typeof opts === 'function' ? opts() : opts
const pendingValueOptions = computed(() => {
  if (['is empty', 'is not empty'].includes(pendingFilter.value.op)) return null
  return _resolveOpts(pendingCol.value?.valueOptions) || null
})
const showValueInput    = computed(() => !NO_VALUE_OPS.has(pendingFilter.value.op))
const isDateInputOp     = computed(() => DATE_INPUT_OPS.has(pendingFilter.value.op))
const isNumberInputOp   = computed(() => NUMBER_INPUT_OPS.has(pendingFilter.value.op))
const isRangeOp         = computed(() => RANGE_OPS.has(pendingFilter.value.op))
const canAdd = computed(() => {
  if (NO_VALUE_OPS.has(pendingFilter.value.op)) return true
  if (isRangeOp.value) return !!(pendingFilter.value.value?.start && pendingFilter.value.value?.end)
  return String(pendingFilter.value.value).trim().length > 0
})

watch(() => pendingFilter.value.col, newCol => {
  const col = props.columns.find(c => c.key === newCol)
  const ops = col?.ops || DEFAULT_OPS
  pendingFilter.value.op = ops[0]
  if (RANGE_OPS.has(ops[0])) {
    pendingFilter.value.value = { start: '', end: '' }
  } else {
    const opts = _resolveOpts(col?.valueOptions)
    pendingFilter.value.value = opts ? opts[0] : ''
  }
})

watch(() => pendingFilter.value.op, newOp => {
  if (RANGE_OPS.has(newOp)) {
    pendingFilter.value.value = { start: '', end: '' }
  } else if (NO_VALUE_OPS.has(newOp)) {
    pendingFilter.value.value = ''
  } else {
    const opts = _resolveOpts(pendingCol.value?.valueOptions)
    if (opts && !opts.includes(pendingFilter.value.value)) pendingFilter.value.value = opts[0]
    else if (DATE_INPUT_OPS.has(newOp) && !pendingFilter.value.value) pendingFilter.value.value = ''
  }
})

// reset pending col when filterableColumns change (different views)
watch(_firstFilterable, v => { if (!pendingFilter.value.col) pendingFilter.value.col = v })

const addFilter = () => {
  if (!canAdd.value) return
  const { col, op, value } = pendingFilter.value
  emit('update:activeFilters', [...props.activeFilters, { col, op, value }])
  filterPanelOpen.value = false
}

// ── Chip helpers (re-implement locally since composable isn't injected) ───────
const _prettyDate = iso => {
  try {
    const [y, m, d] = iso.split('-').map(Number)
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  } catch { return iso }
}

const chipLabel = f => {
  const col   = props.columns.find(c => c.key === f.col)
  const label = col?.label || f.col
  if (NO_VALUE_OPS.has(f.op)) return `${label} ${f.op}`
  if (RANGE_OPS.has(f.op) && f.value?.start && f.value?.end) {
    return `${label} ${f.op} ${_prettyDate(f.value.start)} – ${_prettyDate(f.value.end)}`
  }
  if (DATE_INPUT_OPS.has(f.op) && f.value) {
    return `${label} ${f.op} ${_prettyDate(f.value)}`
  }
  return `${label} ${f.op} "${f.value}"`
}

const removeFilter = i => emit('update:activeFilters', props.activeFilters.filter((_, idx) => idx !== i))
const clearAllFilters = () => emit('update:activeFilters', [])

// ── Column picker actions ─────────────────────────────────────────────────────
const toggleCol = key => {
  const next = props.visibleKeys.includes(key)
    ? props.visibleKeys.filter(k => k !== key)
    : [...props.visibleKeys, key]
  emit('update:visibleKeys', next)
}

// ── Drag-and-drop column reordering ──────────────────────────────────────────
const dragKey     = ref(null)
const dragOverKey = ref(null)

const onDragStart = (e, key) => { dragKey.value = key; e.dataTransfer.effectAllowed = 'move' }
const onDragOver  = (e, key) => { e.preventDefault(); dragOverKey.value = key }
const onDragLeave = ()       => { dragOverKey.value = null }
const onDrop = (e, key) => {
  e.preventDefault()
  const src = dragKey.value
  if (src && src !== key) {
    const order = [...props.columnOrder]
    const fi = order.indexOf(src), ti = order.indexOf(key)
    if (fi >= 0 && ti >= 0) { order.splice(fi, 1); order.splice(ti, 0, src) }
    emit('update:columnOrder', order)
  }
  dragKey.value = dragOverKey.value = null
}
const onDragEnd = () => { dragKey.value = dragOverKey.value = null }

// ── Click-outside to close panels ────────────────────────────────────────────
const rootRef = ref(null)
const handleOutsideClick = e => {
  if (rootRef.value && !rootRef.value.contains(e.target)) {
    filterPanelOpen.value = false
    colPickerOpen.value   = false
  }
}
onMounted(()  => document.addEventListener('click', handleOutsideClick))
onUnmounted(() => document.removeEventListener('click', handleOutsideClick))

const searchInputEl = ref(null)

// "s" (see AppRoot.vue's global shortcuts) jumps straight to this search box
// from anywhere on a page that has one, without a navigation.
const activeSearchFocus = useActiveSearchFocus()
const focusSearch = () => searchInputEl.value?.focus()
// Activated/Deactivated too: AppRoot's <keep-alive> hides (not unmounts) the
// admin panel when you visit the Dashboard, so without these a kept-alive
// toolbar never reclaims "s" after the Dashboard's own toolbar released it.
onMounted(() => activeSearchFocus.register(focusSearch))
onActivated(() => activeSearchFocus.register(focusSearch))
onDeactivated(() => activeSearchFocus.unregister(focusSearch))
onUnmounted(() => activeSearchFocus.unregister(focusSearch))
if (props.searchKey) {
  onMounted(() => activeSearchFocus.registerNamed(props.searchKey, focusSearch))
  onUnmounted(() => activeSearchFocus.unregisterNamed(props.searchKey, focusSearch))
}
</script>
